import { getLanguage, Language } from '../i18n';

export const DateFormat = {
  'Monday, 01 January 2026': 'Monday, 01 January 2026',
  'Monday, 01 January 2026 at 16:16': 'Monday, 01 January 2026 at 16:16',
  'Mon 01 Jan': 'Mon 01 Jan',
  'Mon 01 Jan, 16:16': 'Mon 01 Jan, 16:16',
  '01 Jan 2026': '01 Jan 2026',
} as const;

export type DateFormat = keyof typeof DateFormat;

export const formatDate = (date: string | Date, options: { language?: Language; format?: DateFormat } = {}) => {
  const { language = getLanguage(), format = DateFormat['Mon 01 Jan'] } = options;

  switch (format) {
    case DateFormat['Monday, 01 January 2026']:
      return formatDateFull(date, language, false);
    case DateFormat['Monday, 01 January 2026 at 16:16']:
      return formatDateFull(date, language, true);
    case DateFormat['Mon 01 Jan']:
      return formatDateShort(date, language);
    case DateFormat['Mon 01 Jan, 16:16']:
      return formatDateShort(date, language, true);
    case DateFormat['01 Jan 2026']:
      return formatDateDetails(date, language, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
  }
};

/**
 * ```ts
 * en → 'Tue 24 Feb'
 * en + includeHour → 'Tue 24 Feb, 16:16'
 * nl → `Di 24 Feb`
 * nl + includeHour → 'Di 24 Feb, 16:16'
 * ```
 */
export const formatDateShort = (dateString: string | Date, language?: Language, includeHour?: boolean) => {
  const locale = languageToLocale[language ?? getLanguage()];
  const formattedLocale = locale === 'en-US' ? 'en-UK' : locale;

  try {
    const date = new Date(dateString);
    const formattedDate = new Intl.DateTimeFormat(formattedLocale, {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: includeHour ? '2-digit' : undefined,
      minute: includeHour ? '2-digit' : undefined,
      hour12: includeHour ? false : undefined,
    }).format(date);

    return formattedDate;
  } catch {
    return 'Invalid Date';
  }
};

/**
 * ```ts
 * en → 'TUESDAY, 24 FEBRUARY 2026'
 * en + includeHour → 'TUESDAY, 24 FEBRUARY 2026 AT 16:24'
 * en + includeHour + uppercase(false) → 'Tuesday, 24 February 2026 at 16:24'
 * nl → 'DINSDAG 24 FEBRUARI 2026'
 * nl + includeHour → 'DINSDAG 24 FEBRUARI 2026 OM 16:16'
 * nl + includeHour + uppercase(false) → 'dinsdag 24 februari 2026 om 16:16'
 * ```
 */
const formatDateFull = (dateString: string | Date, language: Language, includeHour = false) => {
  const locale = languageToLocale[language ?? getLanguage()];
  const formattedLocale = locale === 'en-US' ? 'en-UK' : locale;

  try {
    const date = new Date(dateString);
    const formatted = new Intl.DateTimeFormat(formattedLocale, {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: includeHour ? '2-digit' : undefined,
      minute: includeHour ? '2-digit' : undefined,
      hour12: includeHour ? false : undefined,
    }).format(date);

    return formatted;
  } catch {
    return 'Invalid Date';
  }
};

const formatDateDetails = (dateString: string | Date, language: Language, options: Intl.DateTimeFormatOptions) => {
  const locale = languageToLocale[language ?? getLanguage()];
  const formattedLocale = locale === 'en-US' ? 'en-UK' : locale;

  try {
    const date = new Date(dateString);
    const formatted = new Intl.DateTimeFormat(formattedLocale, options).format(date);

    return formatted;
  } catch {
    return 'Invalid Date';
  }
};

const languageToLocale: Record<Language, string> = {
  nl: 'nl-NL',
  en: 'en-US',
};
